<template>
  <div>
    <serveur-info :erreur="error" />
    <my-notification :icon="'building'">
      <template #title>
        {{ $t('compCarbonFormsBuildingsMain.title-1') }}
      </template>
      <p>
        {{ $t('compCarbonFormsBuildingsMain.info-1') }}
        <strong>{{ GHGIyear }}</strong
        >.
      </p>
    </my-notification>
    <br />
    <help-file-format :module="module" />
    <b-notification :has-icon="true" icon="info" :closable="false">
      {{ $t('compCarbonFormsBuildingsMain.info-3') }}
      <span v-html="$t('compCarbonFormsBuildingsMain.info-8')"></span>
    </b-notification>

    <upload
      v-model="source"
      :disabled="isSubmitted"
      :module="module"
      :infos="infos"
      :callback="callback"
      :formatDescription="formatDescription"
      @update="$emit('is-modified', true)"
    />

    <buildings-table
      :items="buildings"
      @edit="edit"
      @update="$emit('is-modified', true)"
      @del="del"
      data-testid="buildings-table"
    />

    <building-modal
      v-if="building !== null"
      :displayModal="displayModal"
      :building="building"
      :buildings="buildings"
      :modeCreation="modeCreation"
      @close="closeModal"
      @update="update"
      @add="add"
    />
    <br />
    <nav class="level">
      <div class="level-left"></div>
      <div class="level-right">
        <div class="buttons">
          <b-button
            class="button"
            icon-left="plus"
            :disabled="isSubmitted"
            @click="displayAddForm()"
          >
            {{ $t('add') }}
          </b-button>
          <b-button
            class="level-item"
            data-testid="buildings-save"
            :icon-left="validSavedIcon"
            :disabled="!flags.isModified"
            type="is-primary"
            @click="validate()"
          >
            <span v-if="authStore.isAuthenticated">{{ $t('save') }}</span>
            <span v-else>{{ $t('validate') }}</span>
          </b-button>
        </div>
      </div>
    </nav>
  </div>
</template>

<script>
import Building from '@/models/carbon/Building.js'
import Modules from '@/models/Modules.js'
import { useAdminStore } from '@/stores/admin.js'
import { useAuthenticationStore } from '@/stores/authentication.js'
import { useGHGIStore } from '@/stores/ghgi.js'

import _ from 'lodash'

export default {
  name: 'Main',
  components: {
    'my-notification': () => import('@/components/MyNotification.vue'),
    'serveur-info': () => import('@/components/ServeurInfo.vue'),
    upload: () => import('@/components/carbon/forms/Upload.vue'),
    'help-file-format': () =>
      import('@/components/carbon/forms/HelpFileFormat.vue'),
    'building-modal': () =>
      import('@/components/carbon/forms/buildings/Modal.vue'),
    'buildings-table': () =>
      import('@/components/carbon/forms/buildings/Table.vue')
  },
  $_veeValidate: {
    validator: 'new'
  },
  props: {
    flags: {
      type: Object
    }
  },
  data () {
    return {
      error: '',
      source: null,
      modeCreation: false,
      displayModal: false,
      building: null,
      activeTab: 0
    }
  },
  setup () {
    const adminStore = useAdminStore()
    const authStore = useAuthenticationStore()
    const ghgiStore = useGHGIStore()
    return { adminStore, authStore, ghgiStore }
  },
  computed: {
    validSavedIcon: function () {
      let icon = null
      if (this.authStore.isAuthenticated) {
        icon = 'save'
      } else {
        icon = 'check'
      }
      return icon
    },
    infos: function () {
      let totalInvalid = this.ghgiStore.invalidItems(
        this.module,
        this.source
      ).length
      let totalValid = this.buildings.length - totalInvalid
      let value1 = this.$options.filters.splitNumber(totalValid) + ' / '
      value1 += this.$options.filters.splitNumber(totalValid + totalInvalid)
      return [
        {
          label: this.$t('compCarbonFormsBuildingsMain.label-1'),
          value: value1
        },
        {
          label: this.$t('compCarbonFormsBuildingsMain.label-2'),
          tooltip: this.$t('compCarbonFormsBuildingsMain.tooltip-1'),
          value:
            this.$options.filters.splitNumber(this.totalElectricity / 1000) +
            ' MWh'
        },
        {
          label: this.$t('compCarbonFormsBuildingsMain.label-3'),
          tooltip: this.$t('compCarbonFormsBuildingsMain.tooltip-1'),
          value:
            this.$options.filters.splitNumber(this.totalHeating / 1000) + ' MWh'
        }
      ]
    },
    formatDescription: function () {
      return function (fields) {
        if (fields.length <= 3) {
          return [Building.refrigerantFormatDescription, Modules.REFRIGERANTS]
        } else {
          return [Building.energyFormatDescription, 'energies']
        }
      }
    },
    module: function () {
      return Modules.BUILDINGS
    },
    isSubmitted: function () {
      return this.ghgiStore.item.isSubmitted
    },
    GHGIyear: function () {
      return this.ghgiStore.item.year
    },
    buildings: function () {
      return this.ghgiStore.items(this.module).filter((item) => {
        if (this.source === null) {
          return true
        } else {
          return item.source === this.source
        }
      })
    },
    allFilesEnergies: function () {
      return this.allFiles.filter((obj) => obj.filetype === 'energies')
    },
    allFilesRefrigerants: function () {
      return this.allFiles.filter(
        (obj) => obj.filetype === Modules.REFRIGERANTS
      )
    },
    totalElectricity: function () {
      let validBuildings = this.buildings.filter(
        (obj) => obj.score !== 2 && obj.electricity
      )
      return validBuildings
        .map((obj) => obj.getElectricityConsumption())
        .reduce((a, b) => parseFloat(a) + parseFloat(b), 0.0)
        .toFixed(0)
    },
    totalHeating: function () {
      let validBuilding = this.buildings.filter(
        (obj) => obj.score !== 2 && obj.heatings
      )
      return validBuilding
        .map((obj) => obj.getTotalHeatingConsumption())
        .reduce((a, b) => parseFloat(a) + parseFloat(b), 0.0)
        .toFixed(0)
    }
  },
  methods: {
    callback: function (data, format, source) {
      this.error = null
      this.isLoading = true
      let allTags = this.adminStore.allTagCategories
        .map((obj) => obj.tags)
        .flat()
      if (format === 'energies') {
        let newBuildings = Building.createFromLines(
          data,
          format,
          allTags,
          source
        )
        const allBuildingsInDB = this.adminStore.allBuildings
        for (let newBuilding of newBuildings) {
          // if building is in database already, retrieve id to avoid duplicate
          let buildingIsInDB = allBuildingsInDB.filter(
            (obj) => obj.name === newBuilding.name
          )
          if (buildingIsInDB.length) {
            newBuilding.id = buildingIsInDB[0].id
          }

          let allBuildingNames = this.ghgiStore
            .items(this.module)
            .map((obj) => obj.name)
          if (allBuildingNames.includes(newBuilding.name)) {
            let msg =
              ' <strong>' +
              newBuilding.name +
              '</strong> : ' +
              this.$t('compCarbonFormsBuildingsMain.message-13')
            this.$buefy.dialog.alert({
              title: this.$t('compCarbonFormsBuildingsMain.message-12'),
              message: msg
            })
          } else {
            this.ghgiStore.addItem({
              module: this.module,
              item: newBuilding
            })
          }
        }
      } else {
        let allBuildingNames = this.ghgiStore
          .items(this.module)
          .map((obj) => obj.name)
        let refrigerants = Building.createFromLines(
          data,
          format,
          allTags,
          source
        )
        let invalidItem = 0
        for (let refrigerant of refrigerants) {
          // Check if building exists in the store, then push new refrigerant
          if (
            allBuildingNames.includes(refrigerant.building) &&
            refrigerant.valid
          ) {
            this.addRefrigerant({
              buildingName: refrigerant.building,
              refrigerant: refrigerant.value
            })
          } else {
            invalidItem++
          }
        }
        if (invalidItem) {
          this.$buefy.dialog.alert({
            title: this.$t('compCarbonFormsBuildingsMain.message-10'),
            message: this.$t('compCarbonFormsBuildingsMain.message-11')
          })
        }
      }
    },
    validate: function () {
      let self = this
      if (this.ghgiStore.invalidItems(this.module).length !== 0) {
        this.$buefy.dialog.confirm({
          title: self.$t('compCarbonFormsBuildingsMain.title-2'),
          message: self.$t('compCarbonFormsBuildingsMain.info-5'),
          confirmText: self.$t('delete'),
          cancelText: self.$t('cancel'),
          canCancel: ['escape', 'button', 'outside'],
          onConfirm: function () {
            self.ghgiStore.deleteInvalidItems(self.module)
            self.subValidate()
          }
        })
      } else {
        this.subValidate()
      }
    },
    subValidate: function () {
      if (this.authStore.isAuthenticated) {
        const loadingComponent = this.$buefy.loading.open({
          container: null
        })
        this.ghgiStore
          .saveItems(this.module)
          .then((data) => {
            loadingComponent.close()
            this.error = ''
            this.$emit('is-modified', false)
            this.$emit('is-valid', this.ghgiStore.items(this.module).length > 0)
            window.scrollTo(0, 0)
            this.$buefy.toast.open({
              type: 'is-light',
              message: this.$t('compCarbonFormsBuildingsMain.save-msg')
            })
            this.source = null
          })
          .catch((error) => {
            loadingComponent.close()
            this.error = error.response.data
            this.$emit('is-valid', false)
          })
      } else {
        this.$emit('is-valid', this.ghgiStore.items(this.module).length > 0)
        this.$emit('is-modified', false)
      }
    },
    displayAddForm: function () {
      this.modeCreation = true
      this.building = Building.createEmpty()
      this.displayModal = true
    },
    closeModal: function () {
      this.displayModal = false
      this.building = null
    },
    add: function (building) {
      this.ghgiStore.addItem({
        module: this.module,
        item: building
      })
      this.$emit('is-modified', true)
      this.closeModal()
    },
    update: function (obj) {
      this.ghgiStore.updateItem({
        module: this.module,
        item: obj
      })
      this.$emit('is-modified', true)
      this.closeModal()
    },
    addRefrigerant: function (iobj) {
      const item = this.ghgiStore
        .items(this.module)
        .find((obj) => obj.name === iobj.buildingName)
      let nbuilding = _.cloneDeep(item)
      nbuilding.refrigerants.push(iobj.refrigerant)
      nbuilding.sourceRefrigerant = iobj.source
      this.ghgiStore.updateItem({
        module: this.module,
        item: {
          from: item,
          to: nbuilding
        }
      })
      this.$emit('is-modified', true)
    },
    del: function (items) {
      this.ghgiStore.deleteItems({
        module: this.module,
        items: items
      })
    },
    edit: function (building) {
      this.modeCreation = false
      this.building = building
      this.displayModal = true
    }
  },
  mounted () {
    if (this.authStore.isAuthenticated) {
      const loadingComponent = this.$buefy.loading.open({
        container: null
      })
      this.adminStore.getAllBuildings().then((data) => {
        loadingComponent.close()
      })
    }
  }
}
</script>

<style>
.b-tabs .tabs {
  overflow: visible;
}
.b-tabs .tabs li.is-disabled {
  pointer-events: auto;
}
.b-tabs .tabs li.is-disabled a:active {
  pointer-events: none;
}
span.heating-header {
  margin-right: 15px;
}
.table td {
  vertical-align: middle !important;
}
</style>
